<template>
  <div class="auto-suggestion-editor" @click="hideSuggestions">
    <vue-editor
      ref="editor"
      :id="id"
      v-model="content"
      :editor-toolbar="customToolbar"
      @input="onEditorInput"
      @ready="onEditorReady"
      class="form-control"
      :disabled="disabled"
      :readonly="readonly"
    />

    <ul
      v-if="showSuggestions && filteredSuggestions.length"
      class="suggestion-list"
      :style="{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px` }"
    >
      <li
        v-for="(item, index) in filteredSuggestions"
        :key="index"
        :class="{ active: index === selectedIndex }"
        @mousedown.prevent="insertSuggestion(item)"
      >
        {{ item }}
      </li>
    </ul>
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";

export default {
  name: "AutoSuggestionEditor",
  components: { VueEditor },
  props: {
    id: {
      type: String,
      default: "editor",
    },
    value: {
      type: String,
      default: "",
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Body...",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      content: this.value,
      quill: null,
      showSuggestions: false,
      filteredSuggestions: [],
      selectedIndex: 0,
      dropdownPos: { top: 0, left: 0 },
      customToolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        [{ color: [] }, { background: [] }],
        ["link"],
        ["blockquote", "code-block", "clean"],
      ],
    };
  },

  watch: {
    value(val) {
      if (val !== this.content) this.content = val;
    },
    content(val) {
      this.$emit("input", val);
    },
  },

  methods: {
    onEditorReady(quillInstance) {
      this.quill = quillInstance;

      quillInstance.keyboard.addBinding({ key: 40 }, () => this.navigateSuggestions("down"));
      quillInstance.keyboard.addBinding({ key: 38 }, () => this.navigateSuggestions("up"));
      quillInstance.keyboard.addBinding({ key: 13 }, () => {
        if (this.showSuggestions) {
          const item = this.filteredSuggestions[this.selectedIndex];
          if (item) this.insertSuggestion(item);
          return false;
        }
        return true;
      });
    },

    onEditorInput() {
      const quill = this.quill;
      if (!quill) return;

      const range = quill.getSelection();
      if (!range) return;

      const textBeforeCursor = quill.getText(0, range.index);
      const match = textBeforeCursor.match(/{{\w*$/);

      if (match) {
        const keyword = match[0].replace("{{", "");
        this.filteredSuggestions = this.suggestions.filter((s) => s.toLowerCase().includes(keyword.toLowerCase()));
        this.showSuggestions = this.filteredSuggestions.length > 0;
        this.selectedIndex = 0;
        this.$nextTick(() => this.updateDropdownPosition());
      } else {
        this.showSuggestions = false;
      }
    },

    insertSuggestion(item) {
      const quill = this.quill;
      if (!quill) return;

      const range = quill.getSelection();
      if (!range) return;

      const textBeforeCursor = quill.getText(0, range.index);
      const match = textBeforeCursor.match(/{{\w*$/);
      if (match) {
        const startPos = range.index - match[0].length;
        quill.deleteText(startPos, match[0].length);
        quill.insertText(startPos, item);
        quill.setSelection(startPos + item.length);
      }
      this.showSuggestions = false;
    },

    updateDropdownPosition() {
      const quill = this.quill;
      if (!quill) return;

      const range = quill.getSelection();
      if (!range) return;

      const bounds = quill.getBounds(range.index);

      const editorWrapper = this.$refs.editor.$el;
      const editorContainer = editorWrapper.querySelector(".ql-editor");

      const editorRect = editorContainer.getBoundingClientRect();

      const parentRect = this.$el.getBoundingClientRect();

      this.dropdownPos = {
        top: bounds.top + bounds.height + (editorRect.top - parentRect.top) - editorContainer.scrollTop,
        left: bounds.left + (editorRect.left - parentRect.left),
      };
    },

    navigateSuggestions(direction) {
      if (!this.showSuggestions) return;
      const len = this.filteredSuggestions.length;
      if (direction === "down") {
        this.selectedIndex = (this.selectedIndex + 1) % len;
      } else if (direction === "up") {
        this.selectedIndex = (this.selectedIndex - 1 + len) % len;
      }
    },

    hideSuggestions() {
      this.showSuggestions = false;
    },
  },
};
</script>

<style scoped>
.auto-suggestion-editor {
  position: relative;
  width: 100%;
  display: inline-block;
}

.form-control {
  border: none;
  border-radius: 6px;
  min-height: 45vh;
  padding: 0;
  font-family: monospace;
}

.suggestion-list {
  position: absolute;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  list-style: none;
  padding: 4px 0;
  margin: 0;
  width: max-content;
  min-width: 180px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
}

.suggestion-list li {
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
}

.suggestion-list li:hover,
.suggestion-list li.active {
  background: #f0f0f0;
}

::v-deep(.ql-container.ql-snow) {
  border: none !important;
}
</style>
